import { colors } from '@ardoq/design-tokens';
import {
  ChangeEvent,
  HTMLProps,
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
} from 'react';
import styled from 'styled-components';
import {
  FormErrorIcon,
  FormWarningIcon,
  InputStyleProps,
  InputWrapper,
  ReadOnlyStyle,
  commonInputStyle,
} from '../../atoms';
import { useTextAreaAutosize } from '../../utils';
import { Field } from '../Field/Field';
import { CommonFieldProps } from '../Field/types';

const TextAreaStyle = styled.textarea<
  InputStyleProps & { $fixedHeight?: boolean; $isResizable?: boolean }
>`
  background-color: ${colors.surfaceDefault};
  ${commonInputStyle};
  resize: none;
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
      background-color: ${colors.surfaceDisabled};
      color: ${colors.textDisabled};
    `}

  ${({ $fixedHeight }) => $fixedHeight && `height: 100%;`}

  ${({ $isResizable }) =>
    $isResizable &&
    `
      resize: vertical;
      height: 100%;
      min-height: 1.5em;
    `}
`;

export type TextAreaProps = CommonFieldProps &
  Pick<
    HTMLProps<HTMLTextAreaElement>,
    | 'aria-label'
    | 'autoComplete'
    | 'autoFocus'
    | 'onFocus'
    | 'onKeyDown'
    | 'rows'
    | 'className'
  > & {
    isReadOnly?: boolean;
    isResizable?: boolean;
    placeholder?: string;
    defaultValue?: string;
    value?: string;
    errorMessage?: string;
    hintMessage?: string;
    hasError?: boolean;
    hideWarningIcon?: boolean;
    fixedHeight?: boolean;
    tabIndex?: -1;
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    /**
     * This will be called when the input is blurred and the value has changed.
     */
    onCommit?: (value: string) => void;
    onValueChange?: (value: string) => void;
  };
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      isDisabled,
      isReadOnly,
      isResizable,
      indicator,
      warningMessage,
      errorMessage,
      helperText,
      hintMessage,
      label,
      hasWarning: hasWarningProp,
      hideWarningIcon,
      value,
      fixedHeight,
      rows = 3,
      onChange,
      onCommit,
      onValueChange,
      popoverHelpContent,
      dataTestId,
      ...props
    },
    forwardedRef
  ) => {
    const { ref } = useTextAreaAutosize(value);
    useImperativeHandle(forwardedRef, () => ref.current!);

    const isCommitedRef = useRef(false);

    const generatedId = useId();
    const id = props.inputId ?? generatedId;

    const hasError = props.hasError || Boolean(errorMessage);
    const hasWarning = !hasError && (hasWarningProp || Boolean(warningMessage));
    const styleProps: InputStyleProps = {
      $isDisabled: isDisabled,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hideWarningIcon: hideWarningIcon,
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      onChange?.(event);
      onValueChange?.(newValue);
      isCommitedRef.current = true;
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (isCommitedRef.current && onCommit) {
        onCommit(event.target.value);
        isCommitedRef.current = false;
      }
    };

    return (
      <Field
        label={label}
        inputId={id}
        helperText={helperText}
        isDisabled={isDisabled}
        indicator={indicator}
        hintMessage={hintMessage}
        dataTestId={dataTestId}
        errorMessage={errorMessage}
        warningMessage={warningMessage}
        popoverHelpContent={popoverHelpContent}
      >
        <InputWrapper {...styleProps}>
          {isReadOnly ? (
            <ReadOnlyStyle>{value}</ReadOnlyStyle>
          ) : (
            <TextAreaStyle
              {...props}
              {...styleProps}
              onBlur={handleBlur}
              onChange={handleChange}
              value={value}
              id={id}
              ref={fixedHeight || isResizable ? undefined : ref}
              $fixedHeight={fixedHeight}
              $isResizable={isResizable}
              disabled={isDisabled}
              rows={rows}
              aria-required={
                props.isRequired || indicator === 'required'
                  ? 'true'
                  : undefined
              }
            />
          )}
          {hasError && <FormErrorIcon />}
          {!hasError && hasWarning && !hideWarningIcon && <FormWarningIcon />}
        </InputWrapper>
      </Field>
    );
  }
);
